import './elsieAiSummary.scss';
import { useEffect, useState, useMemo } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import { Feature } from 'common/models/page-config.models';
import { detailPaths } from '../../../modules/insights/constants';
import { getComplexIconRaw, KWELLO_ICON } from 'common/components/icon/KwelloSvgIcon';
import { useAppSelector } from '../../hooks/redux';
import { selectElsieSummaryData } from '../../../common/store/slices/elsie-ai.slice';
import useElsieAI from '../../../common/hooks/useElsieAI';
import { ElsieAISummaryFlyout } from './ElsieAISummaryFlyout';

import { selectAppliedFilters } from '../../../common/store/slices/common-filters.slice';
import { RootState } from '../../../common/store';

import { selectChartFilters } from '../../../common/store/slices/chart-filters.slice';
import { selectDatasetQuestion } from 'common/store/slices/dataset.slice';
import { DATASET_INITIAL_ID } from 'common/constants';
import { useCompareUrlWithProjectId } from 'common/hooks/route';
import { selectDisableAIOperations } from 'common/store/slices/elsie-ai.slice';
import useNavigationMenus from 'common/hooks/useMenus';
import { useParams } from 'react-router-dom';
import { SurveyCitation, isSurveyCitation } from 'common/models/elsie-ai.model';

export function ElsieAISummaryButton() {
  const { projectId } = useParams();
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const { hasFeature } = useFeatureFlags();
  const { hasElsieMenuItem } = useNavigationMenus();
  const hasElsieAiSummaryButton = hasElsieMenuItem && hasFeature(Feature.ElsieAiSummaryButton);
  const summaryData = useAppSelector(selectElsieSummaryData);
  const disableAIOperations = useAppSelector(selectDisableAIOperations);
  const { compareUrl } = useCompareUrlWithProjectId();
  const filters = useAppSelector((root: RootState) =>
    selectAppliedFilters(root, DATASET_INITIAL_ID)
  );
  const chartFilters = useAppSelector((root: RootState) =>
    selectChartFilters(root, DATASET_INITIAL_ID)
  );
  const datasetQuestion = useAppSelector((state: RootState) =>
    selectDatasetQuestion(state, DATASET_INITIAL_ID, projectId ?? '')
  );

  const { messages, onAddNewMessage, onDeleteAll, closeCitations, getCitations } = useElsieAI({
    kwelloIds: summaryData?.kwelloIds || [],
    version: 1,
  });

  const citations = getCitations({ citationData: summaryData?.referenceData });
  const surveyCitations = useMemo<SurveyCitation[]>(
    () => (citations ?? []).filter(isSurveyCitation),
    [citations]
  );
  useEffect(() => {
    if (Object.keys(filters)?.length || chartFilters?.length || datasetQuestion?.id) {
      onDeleteAll();
    }
  }, [filters, chartFilters, onDeleteAll, datasetQuestion]);

  if (!detailPaths.find((path) => compareUrl(path))) {
    return null;
  }

  if (!hasElsieAiSummaryButton) {
    return null;
  }

  const onClick = async () => {
    setFlyoutOpen(!flyoutOpen);
    if (!messages.length) {
      await onAddNewMessage('', 'summarize');
    }
  };

  const onClose = () => {
    setFlyoutOpen(false);
  };

  return (
    <>
      <Button
        themeColor="primary"
        className="elsie-ai-summary-button"
        size="small"
        rounded="full"
        onClick={onClick}
        svgIcon={getComplexIconRaw(KWELLO_ICON.LIGHT_BULB_ICON)}
        disabled={disableAIOperations || !summaryData?.kwelloIds?.length}
      >
        ElsieAI Summary
      </Button>
      <div className="elsie-ai-summary-flyout-container">
        <ElsieAISummaryFlyout
          flyoutOpen={flyoutOpen}
          setFlyoutOpen={setFlyoutOpen}
          messages={messages}
          onClose={onClose}
          closeCitations={closeCitations}
          onDeleteAll={onDeleteAll}
          citations={surveyCitations ?? []}
        />
      </div>
    </>
  );
}
